import React from 'react';
import { useStyles, cx } from '../../../lib/styles';
import { Brand } from '../../../lib/theme';

import selectInputStyles from './SelectInput_styles';
import InputStatus, { InputValidationStatus } from '../Input/InputStatus';
import InputLabel from '../Input/InputLabel';

export interface SelectInputProps {
  /** The brand to use for colouring the select input. */
  brand?: Brand;

  /** Any additional classnames for the input element. */
  className?: string;

  /** Any additional classnames for the label element. */
  labelClassName?: string;

  /** Any additional classnames for the input element. */
  containerClassName?: string;

  /** Whether a selection is required. */
  required?: boolean;

  /** The label of the input. */
  label?: string;

  /** The name of the form element. */
  name?: string;

  /** The current selection. */
  value?: any;

  /** An event listener for when a new value is selected. */
  onChange?: (value: any) => any;

  /** An event listener for when the input is blurred. */
  onBlur?: () => any;

  /** An event listener for when the input is focused. */
  onFocus?: () => any;

  /** The validation status of the input. */
  status?: InputValidationStatus;

  /** The validation status message to display if the status is set. */
  statusMessage?: string;

  /** The text placeholder to display in the input. */
  placeholder?: string;

  /** Whether to auto-focus the input when it first appears. */
  autoFocus?: boolean;

  /** Whether to show the input as disabled. */
  disabled?: boolean;

  /** A mapping of the select option labels to their value. */
  options: { [label: string]: any };
}

/** A dropdown input. */
export const SelectInput = (props: SelectInputProps) => {
  const {
    value = '',
    label,
    containerClassName,
    labelClassName,
    name,
    required,
    className,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    autoFocus,
    disabled,
    options,
    status,
    statusMessage,
  } = props;
  const styles = useStyles(selectInputStyles(props));

  return (
    <div className={containerClassName}>
      {label && (
        <InputLabel
          className={labelClassName}
          required={required}
          status={status}
          htmlFor={name}
        >
          {label}
        </InputLabel>
      )}

      <select
        aria-label={label}
        value={value}
        name={name}
        required={required}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={({ target: { value } }) => onChange && onChange(value)}
        onBlur={onBlur}
        onFocus={onFocus}
        className={cx('SelectInput', styles.container, className)}
      >
        {placeholder && <option value={''}>{placeholder}</option>}
        {Object.keys(options).map(label => (
          <option key={label} value={options[label]}>
            {label}
          </option>
        ))}
      </select>

      {status && statusMessage && (
        <InputStatus message={statusMessage} status={status} />
      )}
    </div>
  );
};

export default SelectInput;
